import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import { orderBy } from 'lodash';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader';
import ModuleContentList from '../../components/ModuleContentList';
import ContractsList from '../../components/ContractsList';
import SearchBar from '../../components/SearchBar';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import { search } from '../../actions/search';
import { fetchTasks } from '../../actions/tasks';
import ContractsTasksList from '../../components/ContractsTasksList';
import { fetchContracts } from '../../actions/contracts';
import theme from '../../theme.style';
import styles from './ContractListing.style';
import { hasRole } from '../../utils/auth';
import useAPI from '../../hooks/useAPI';
import { ContractFilterStatus } from '../../constants';

const ContractListing = () => {
  const dispatch = useDispatch();

  const [query, setQuery] = useState('');
  const [filterState, setFilterState] = useState({});
  const [sortColumn, setSortColumn] = useState('');
  const [sortAscending, setSortAscending] = useState(false);

  const selector = state => {
    if (query) {
      return Object.values(get(state, 'search.results.contracts', {}));
    }

    const stateContracts = get(state, 'pagination.contracts.results', []).map(tokenId => {
      return get(state, `entities.contracts.${tokenId}`);
    });

    const filtered = stateContracts.filter(el => {
      return el != null;
    });
    const sortedItems = orderBy(
      filtered,
      [
        contract =>
          contract &&
          (sortColumn === 'partner'
            ? contract.partner?.name?.toLowerCase()
            : contract[sortColumn]?.toLowerCase()),
      ],
      [sortAscending ? 'asc' : 'desc'],
    );
    if (sortedItems.length < get(state, `pagination.contracts.totalCount`, 0)) {
      return sortedItems.concat(new Array(1));
    }
    return sortedItems;
  };

  const tasks = useSelector(state => Object.values(get(state, 'entities.tasks', {})));

  const totalCount = useSelector(state => get(state, `pagination.contracts.totalCount`, 0));

  const contractsAPI = useAPI(
    () => {
      if (query) {
        return search(['contracts'], query);
      }

      return fetchContracts(filterState);
    },
    [filterState, query],
    {
      fetchOnMount: false,
      fetchOnDependenciesChange: true,
      selector,
    },
  );

  const onStatusChange = async (event, option) => {
    setFilterState(prevState => ({
      ...prevState,
      start: 0,
      filterStatus: option.status,
    }));
  };

  const onSiloChange = async (event, option) => {
    setFilterState(prevState => ({
      ...prevState,
      start: 0,
      filterSilo: option.silo,
    }));
  };

  const filterOptions = [
    { key: 0, text: 'All Contracts', status: '' },
    { key: 1, text: 'In Setup', status: ContractFilterStatus.InSetup },
    { key: 2, text: 'Setup Complete', status: ContractFilterStatus.SetupComplete },
    { key: 3, text: 'Active', status: ContractFilterStatus.Active },
    { key: 4, text: 'Expired', status: ContractFilterStatus.Expired },
  ];

  const siloOptions = [
    { key: 0, text: 'Any', silo: '' },
    { key: 1, text: '1P-AZURE', silo: '1p-azure' },
    { key: 2, text: '3P-TLA', silo: '3p-tla' },
  ];

  useEffect(() => {
    if (!hasRole('partner'))
      dispatch(
        fetchTasks(['contracts', 'digitizedcontracts', 'agreements', 'contractattachments']),
      );
  }, [dispatch]);

  const handleClear = () => {
    setQuery('');
  };

  const handleSearch = async queryText => {
    setQuery(queryText);
  };

  const handleColumnClick = (event, column) => {
    setSortAscending(!sortAscending);
    setSortColumn(column.fieldName);
  };

  const onScrollBottom = useCallback(() => {
    if (contractsAPI.isLoading) {
      return;
    }

    if (contractsAPI.data.length === totalCount) {
      return;
    }

    setFilterState(prevState => ({
      ...prevState,
      start: contractsAPI.data.length - 1,
      count: 20,
    }));
  }, [contractsAPI]);

  return (
    <>
      <PageTitle>Contracts</PageTitle>
      <Main>
        {tasks && tasks.length > 0 && (
          <Module>
            <ModuleHeader>Action Required</ModuleHeader>
            <ModuleContentList>
              <ContractsTasksList items={tasks} isHeaderVisible={false} noBorderBottom />
            </ModuleContentList>
          </Module>
        )}
        <div className={styles.searchSection}>
          <SearchBar
            placeholder="Search contracts"
            ariaLabel="Search"
            underlined
            onChange={handleSearch} // when you type
            onClear={handleClear} // when you click X
            data-testid="search-input"
            className={styles.search}
          />
          <Dropdown
            placeholder="All Contracts"
            label="Status"
            options={filterOptions}
            className={styles.filter}
            styles={{
              title: { backgroundColor: theme.palette.neutralWhite },
              dropdown: { fontWeight: 'normal' },
              dropdownItem: { fontWeight: 'normal' },
            }}
            onChange={onStatusChange}
          />
          {hasRole('msft') && (
            <Dropdown
              options={siloOptions}
              label="Silo"
              className={styles.filter}
              placeholder="All Silos"
              styles={{
                title: { backgroundColor: theme.palette.neutralWhite },
                dropdown: { fontWeight: 'normal' },
                dropdownItem: { fontWeight: 'normal' },
              }}
              onChange={onSiloChange}
            />
          )}
        </div>

        {!!query && (
          <div className={styles.resultsMessage}>
            <Text as="h4" block>
              {contractsAPI.data.length} {contractsAPI.data.length === 1 ? 'Result' : 'Results'}
            </Text>
            {contractsAPI.data.length === 0 && (
              <Text as="p" block className={styles.noResultsSentence}>
                Try searching by contract name, contract ID, or partner name
              </Text>
            )}
          </div>
        )}

        <Module>
          <ModuleContentList>
            <ContractsList
              shimmered
              enableShimmer={contractsAPI.isLoading && !contractsAPI.data.length}
              items={contractsAPI.data}
              noBorderBottom
              onShouldVirtualize={() => false}
              className={styles.scrollablePane}
              sortColumn={sortColumn}
              sortAscending={sortAscending}
              handleColumnClick={handleColumnClick}
              onScrollBottom={onScrollBottom}
              compact
              compactRows={false}
            />
          </ModuleContentList>
        </Module>
      </Main>
    </>
  );
};

export default ContractListing;
